/**
 * IndexedDB offline store for Peekbox.
 *
 * Uses the `idb` library for a promise-based API over IndexedDB.
 * Stores a local mirror of boxes and items for offline reads,
 * plus a pending-mutations queue for offline writes.
 *
 * @module offline/db
 */

import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import type { Box, Item } from '@shared/types';

// ── Schema ──────────────────────────────────────────────────────

export interface PendingMutation {
  id?: number;
  method: 'POST' | 'PUT' | 'DELETE';
  url: string;
  body?: string;
  createdAt: number;
  retries: number;
  status: 'pending' | 'in-flight' | 'failed';
}

interface MetaEntry {
  key: string;
  value: unknown;
}

interface PeekboxDBSchema extends DBSchema {
  boxes: {
    key: string;
    value: Box;
    indexes: { householdId: string };
  };
  items: {
    key: string;
    value: Item;
    indexes: { boxId: string };
  };
  'pending-mutations': {
    key: number;
    value: PendingMutation;
    indexes: { createdAt: number };
  };
  meta: {
    key: string;
    value: MetaEntry;
  };
}

// ── Constants ───────────────────────────────────────────────────

const DB_NAME = 'peekbox-offline';
const DB_VERSION = 1;

// ── Database singleton ──────────────────────────────────────────

let dbPromise: Promise<IDBPDatabase<PeekboxDBSchema>> | null = null;

export function getDB(): Promise<IDBPDatabase<PeekboxDBSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<PeekboxDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Boxes store
        if (!db.objectStoreNames.contains('boxes')) {
          const boxStore = db.createObjectStore('boxes', { keyPath: 'id' });
          boxStore.createIndex('householdId', 'householdId');
        }

        // Items store
        if (!db.objectStoreNames.contains('items')) {
          const itemStore = db.createObjectStore('items', { keyPath: 'id' });
          itemStore.createIndex('boxId', 'boxId');
        }

        // Pending mutations store
        if (!db.objectStoreNames.contains('pending-mutations')) {
          const mutStore = db.createObjectStore('pending-mutations', {
            keyPath: 'id',
            autoIncrement: true,
          });
          mutStore.createIndex('createdAt', 'createdAt');
        }

        // Meta store
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Reset the DB singleton (useful for testing).
 */
export function resetDB(): void {
  dbPromise = null;
}

// ── Box operations ──────────────────────────────────────────────

export async function getBox(id: string): Promise<Box | undefined> {
  const db = await getDB();
  return db.get('boxes', id);
}

export async function getAllBoxes(): Promise<Box[]> {
  const db = await getDB();
  return db.getAll('boxes');
}

export async function putBox(box: Box): Promise<void> {
  const db = await getDB();
  await db.put('boxes', box);
}

export async function putBoxes(boxes: Box[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('boxes', 'readwrite');
  await Promise.all([...boxes.map((b) => tx.store.put(b)), tx.done]);
}

export async function deleteBox(id: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(['boxes', 'items'], 'readwrite');
  // Delete the box
  await tx.objectStore('boxes').delete(id);
  // Delete all items belonging to the box
  const itemStore = tx.objectStore('items');
  const idx = itemStore.index('boxId');
  let cursor = await idx.openCursor(id);
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
}

// ── Item operations ─────────────────────────────────────────────

export async function getItem(id: string): Promise<Item | undefined> {
  const db = await getDB();
  return db.get('items', id);
}

export async function getItemsByBox(boxId: string): Promise<Item[]> {
  const db = await getDB();
  return db.getAllFromIndex('items', 'boxId', boxId);
}

export async function putItem(item: Item): Promise<void> {
  const db = await getDB();
  await db.put('items', item);
}

export async function putItems(items: Item[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('items', 'readwrite');
  await Promise.all([...items.map((i) => tx.store.put(i)), tx.done]);
}

export async function deleteItem(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('items', id);
}

// ── Meta operations ─────────────────────────────────────────────

export async function getMeta<T = unknown>(key: string): Promise<T | undefined> {
  const db = await getDB();
  const entry = await db.get('meta', key);
  return entry?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  const db = await getDB();
  await db.put('meta', { key, value });
}

// ── Pending mutations ───────────────────────────────────────────

export async function addPendingMutation(mutation: Omit<PendingMutation, 'id'>): Promise<number> {
  const db = await getDB();
  return db.add('pending-mutations', mutation as PendingMutation);
}

export async function getAllPendingMutations(): Promise<PendingMutation[]> {
  const db = await getDB();
  return db.getAllFromIndex('pending-mutations', 'createdAt');
}

export async function updatePendingMutation(mutation: PendingMutation): Promise<void> {
  const db = await getDB();
  await db.put('pending-mutations', mutation);
}

export async function deletePendingMutation(id: number): Promise<void> {
  const db = await getDB();
  await db.delete('pending-mutations', id);
}

export async function getPendingMutationCount(): Promise<number> {
  const db = await getDB();
  return db.count('pending-mutations');
}

// ── Bulk clear ──────────────────────────────────────────────────

export async function clearAll(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(['boxes', 'items', 'pending-mutations', 'meta'], 'readwrite');
  await Promise.all([
    tx.objectStore('boxes').clear(),
    tx.objectStore('items').clear(),
    tx.objectStore('pending-mutations').clear(),
    tx.objectStore('meta').clear(),
    tx.done,
  ]);
}
